import { Card, Classes, Colors, Spinner } from "@blueprintjs/core";
import { FGCustomInput } from "nsitools-react";

import { useCommonHooks } from "../../hooks";

import styled from "styled-components";
import { IThemeData } from "../../theme";
import { formatDate } from "../../utils/formatDate";
import { DataTable2, IDataTable2Props } from "../searchTable/DataTable2";

type verticalAlign = "top" | "middle" | "bottom";

export interface FormDataTableProps<T>
  extends Omit<
    IDataTable2Props<T>,
    | "htmlTableOptions"
    | "showColumnSelector"
    | "translateFunc"
    | "showPageSizeSelector"
  > {
  label?: string;
  firstTdVerticalAlign?: verticalAlign;
  othersTdVerticalAlign?: verticalAlign;
}

const NoDataContainer = styled.div<{ theme: IThemeData }>`
  margin: 0.5rem;
  padding: 0.25rem;
  font-style: italic;
  color: ${Colors.GRAY4};
`;

const TableContainer = styled.div<{
  theme: IThemeData;
  firstTdVerticalAlign?: verticalAlign;
  othersTdVerticalAlign?: verticalAlign;
}>`
  flex: 1;
  display: flex;

  tr {
    border: 1px solid black !important;
  }

  &
    table.${Classes.HTML_TABLE}.${Classes.HTML_TABLE_STRIPED}
    tbody
    tr:nth-child(odd)
    td {
    background: ${(props) => props.theme.tableStrip} !important;
  }

  & table.${Classes.HTML_TABLE}.${Classes.INTERACTIVE} tbody tr:hover td {
    background-color: ${(props) => props.theme.tableRowHover} !important;
  }

  & table tbody tr td:first-child {
    vertical-align: ${(props) =>
      props.firstTdVerticalAlign ?? "middle"} !important;
  }

  & table tbody tr td:not(:first-child) {
    vertical-align: ${(props) =>
      props.othersTdVerticalAlign ?? "middle"} !important;
  }
`;

export function FormDataTable<T>(props: Readonly<FormDataTableProps<T>>) {
  const { tUnsafe, t, theme } = useCommonHooks();

  return (
    <FGCustomInput label={props.label}>
      {() => (
        <TableContainer
          theme={theme}
          firstTdVerticalAlign={props.firstTdVerticalAlign}
          othersTdVerticalAlign={props.othersTdVerticalAlign}
        >
          <DataTable2
            htmlTableOptions={{
              bordered: true,
              condensed: true,
              striped: false,
              interactive: !!props.onRowClick,
            }}
            showColumnSelector={false}
            translateFunc={tUnsafe}
            tableState={props.tableState}
            renderNoData={
              <NoDataContainer> {t("GeneralNoData")}</NoDataContainer>
            }
            formatDate={formatDate}
            customLoadingComponent={
              <Card
                elevation={4}
                style={{
                  padding: "0.25rem",
                  display: "flex",
                }}
              >
                <Spinner size={16} />
                &nbsp;{t("GeneralLoading")}
              </Card>
            }
            {...props}
          />
        </TableContainer>
      )}
    </FGCustomInput>
  );
}
